import { css } from '@emotion/react';
import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useInvestmentsCacheState } from '../../utils/persistedState';
import Button from '../Button';
import { spacing, fontStyles } from '../../styles';
import {
  Portfolios,
  savePortfolio,
  updatePortfolio,
  Portfolio,
  PortfolioMinimal,
  getPlaidPortfolioIds,
} from '../../storage';
import { PortfolioSelectionModal } from '../PortfolioSelectionModal';
import bookmarkActiveIcon from './bookmarkFull.svg';
import bookmarkInactiveIcon from './bookmarkEmpty.svg';

interface Props {
  symbol: string;
  category: string;
  name: string;
  showLabel?: boolean;
  customLabel?: string;
}

// Helper function to create updated portfolio with symbol added/removed
const createUpdatedPortfolio = (portfolio: Portfolio, symbol: string, isRemoving: boolean): Portfolio => {
  const updatedSymbols = isRemoving
    ? portfolio.symbols.filter((s) => s !== symbol)
    : portfolio.symbols.includes(symbol)
      ? portfolio.symbols
      : [...portfolio.symbols, symbol];

  const updatedAllocations = isRemoving
    ? (portfolio.allocations || []).filter((a) => a.symbol !== symbol)
    : portfolio.allocations;

  return {
    ...portfolio,
    symbols: updatedSymbols,
    allocations: updatedAllocations,
  };
};

const BookmarkButton: FC<Props> = ({ symbol, category, name, showLabel = false, customLabel }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [plaidPortfolioIdsList, setPlaidPortfolioIdsList] = useState<string[]>([]);
  const portfolios = Portfolios.useState((s) => s.portfolios);
  const [investmentsCache, setInvestmentsCache] = useInvestmentsCacheState();

  // Fetch all Plaid portfolio IDs on mount
  useEffect(() => {
    const fetchIds = async () => {
      const ids = await getPlaidPortfolioIds();
      setPlaidPortfolioIdsList(ids);
    };
    fetchIds();
  }, []);

  const plaidIdSet = useMemo(() => new Set(plaidPortfolioIdsList), [plaidPortfolioIdsList]);

  // Filter out Plaid portfolios for manual operations (Plaid portfolios can't be manually edited)
  const nonPlaidPortfolios = useMemo(() => portfolios.filter((p) => !plaidIdSet.has(p.id)), [portfolios, plaidIdSet]);

  // Check if symbol is in any portfolio (including Plaid for display purposes)
  const checkBookmarkStatus = useCallback(() => {
    // Add null check for symbols array
    const isInAnyPortfolio = portfolios.some(
      (p) => p.symbols && Array.isArray(p.symbols) && p.symbols.includes(symbol),
    );
    setIsBookmarked(isInAnyPortfolio);
  }, [portfolios, symbol]);

  useEffect(() => {
    checkBookmarkStatus();
  }, [checkBookmarkStatus]);

  const handleClick = () => {
    try {
      // Use nonPlaidPortfolios - Plaid portfolios can't be manually edited
      // If no non-Plaid portfolios exist, create a default one and add the symbol to it
      if (nonPlaidPortfolios.length === 0) {
        createDefaultPortfolio();
        return;
      }

      // If only one non-Plaid portfolio exists, toggle the symbol directly
      if (nonPlaidPortfolios.length === 1) {
        toggleInSinglePortfolio(nonPlaidPortfolios[0]);
        return;
      }

      // If multiple non-Plaid portfolios, show the selection modal
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error handling bookmark click:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const createDefaultPortfolio = async () => {
    try {
      const defaultPortfolio: Omit<Portfolio, 'id'> = {
        title: 'Watchlist',
        strategy: 'Default watchlist',
        symbols: [symbol],
        allocations: [],
      };

      await savePortfolio(defaultPortfolio);

      setInvestmentsCache({ ...investmentsCache, [symbol]: { symbol, category, name } });
      showToast(true, 'Watchlist');
    } catch (error) {
      console.error('Error creating default portfolio:', error);
      toast.error('Failed to add to watchlist');
    }
  };

  const toggleInSinglePortfolio = async (portfolio: PortfolioMinimal) => {
    try {
      // Ensure the portfolio has a symbols array
      const currentSymbols = portfolio.symbols || [];

      // Check if symbol is in portfolio
      const isCurrentlyInPortfolio = currentSymbols.includes(symbol);

      // Get full portfolio data to preserve existing fields using getRawState for consistency
      const currentPortfolios = Portfolios.getRawState().portfolios;
      const fullPortfolio = currentPortfolios.find((p) => p.id === portfolio.id) || {
        id: portfolio.id,
        title: portfolio.title,
        strategy: '',
        symbols: [],
        allocations: [],
      };

      // Create updated portfolio using helper function
      const updatedPortfolio = createUpdatedPortfolio(fullPortfolio, symbol, isCurrentlyInPortfolio);

      // Update the existing portfolio
      const success = await updatePortfolio(updatedPortfolio);

      if (!success) {
        throw new Error(`Failed to update portfolio ${portfolio.id}`);
      }

      setInvestmentsCache({ ...investmentsCache, [symbol]: { symbol, category, name } });

      // Show portfolio-specific toast
      showToast(!isCurrentlyInPortfolio, portfolio.title);
    } catch (error) {
      console.error('Error updating portfolio:', error);
      toast.error('Failed to update watchlist');
    }
  };

  const handleModalSubmit = async (selectedIds: string[]) => {
    try {
      // Fetch fresh portfolio data to avoid stale closures
      const currentPortfolios = Portfolios.getRawState().portfolios;

      // Track whether any updates failed
      let anyFailed = false;
      // Track portfolios that had the symbol removed
      const removedFromPortfolios: string[] = [];

      // Process each portfolio sequentially to avoid race conditions
      for (const portfolio of currentPortfolios) {
        // Make sure the portfolio object is valid
        if (!portfolio || !portfolio.id) {
          console.error('Invalid portfolio object:', portfolio);
          anyFailed = true;
          continue;
        }

        // Ensure the portfolio has a symbols array
        const currentSymbols = Array.isArray(portfolio.symbols) ? portfolio.symbols : [];

        const isSelected = selectedIds.includes(portfolio.id);
        const isCurrentlyInPortfolio = currentSymbols.includes(symbol);

        // Determine if we should update this portfolio
        let shouldUpdatePortfolio = false;

        // Case 1: No portfolios selected - remove from all that have the symbol
        if (selectedIds.length === 0) {
          shouldUpdatePortfolio = isCurrentlyInPortfolio;
        }
        // Case 2: Portfolio is selected but doesn't contain the symbol - add it
        else if (isSelected && !isCurrentlyInPortfolio) {
          shouldUpdatePortfolio = true;
        }
        // Case 3: Portfolio is not selected but contains the symbol - remove it
        else if (!isSelected && isCurrentlyInPortfolio) {
          shouldUpdatePortfolio = true;
        }

        if (shouldUpdatePortfolio) {
          // Determine if we're removing the symbol
          const isRemoving = !isSelected;

          // Create updated portfolio using helper function
          const updatedPortfolio = createUpdatedPortfolio(portfolio, symbol, isRemoving);

          // Keep track if we're removing the symbol
          if (isCurrentlyInPortfolio && !isSelected) {
            removedFromPortfolios.push(portfolio.title);
          }

          // Update the portfolio (updatePortfolio already handles errors internally)
          const success = await updatePortfolio(updatedPortfolio);
          if (!success) {
            anyFailed = true;
          }
        }
      }

      // Update investments cache
      setInvestmentsCache({ ...investmentsCache, [symbol]: { symbol, category, name } });

      // Show toast based on whether operation succeeded and we added to any portfolio
      if (anyFailed) {
        toast.error('Some watchlists could not be updated');
      } else if (selectedIds.length > 0) {
        // Show a single toast for multiple portfolios
        if (selectedIds.length === 1) {
          // If only one portfolio was selected, show a specific message
          const portfolio = currentPortfolios.find((p) => p.id === selectedIds[0]);
          if (portfolio) {
            showToast(true, portfolio.title);
          }
        } else {
          // If multiple portfolios were selected, show a consolidated message
          toast(`${symbol} added to ${selectedIds.length} watchlists`, { toastId: `bookmark-multiple-${Date.now()}` });
        }
      } else if (removedFromPortfolios.length > 0) {
        // Show removed from all portfolios toast
        if (removedFromPortfolios.length === 1) {
          showToast(false, removedFromPortfolios[0]);
        } else {
          toast(`${symbol} was removed from all watchlists`);
        }
      }
    } catch (error) {
      console.error('Error updating portfolios:', error);
      toast.error('Failed to update watchlists');
    }
  };

  const showToast = (wasAdded: boolean, portfolioTitle?: string) => {
    let message;

    if (portfolioTitle) {
      message = wasAdded ? `${symbol} was added to ${portfolioTitle}` : `${symbol} was removed from ${portfolioTitle}`;
    } else {
      message = wasAdded ? 'Added to watchlist' : 'Removed from watchlist';
    }

    // Use a unique toastId for each portfolio message to avoid conflicts
    const toastId = portfolioTitle ? `bookmark-${portfolioTitle}` : 'bookmark';

    if (toast.isActive(toastId)) {
      toast.update(toastId, { render: message });
    } else {
      toast(message, { toastId: toastId });
    }
  };

  const styles = {
    bookmarkButton: css`
      padding: ${spacing.centi};
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0px;
      min-width: 44px;
      min-height: 44px;

      &:focus {
        outline: none;
      }

      @media (max-width: 359px) {
        padding: ${spacing.micro};
      }
    `,
    bookmarkIcon: css`
      height: 22px;
      width: 22px;
      vertical-align: middle;
      filter: var(--icon-filter);

      @media (max-width: 359px) {
        height: 20px;
        width: 20px;
      }

      html[data-theme='dark'] & {
        filter: ${`brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(1%) hue-rotate(81deg) brightness(108%) contrast(102%)`};
      }
    `,
    label: css`
      ${fontStyles.mouse}

      @media (max-width: 359px) {
        font-size: 0.7rem;
      }
    `,
  };

  return (
    <>
      <Button type="button" onClick={handleClick} css={styles.bookmarkButton}>
        <img src={isBookmarked ? bookmarkActiveIcon : bookmarkInactiveIcon} css={styles.bookmarkIcon} alt="Bookmark" />
        {showLabel && <span css={styles.label}>{customLabel || (isBookmarked ? 'Remove' : 'Add')}</span>}
      </Button>

      {/* Use a portal to render the modal at the root level to avoid z-index issues */}
      <PortfolioSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleModalSubmit}
        title="Add to Portfolio"
        symbol={symbol}
        excludePortfolioIds={plaidPortfolioIdsList}
      />
    </>
  );
};

export default BookmarkButton;
